<template>
  <b-overlay :show="loading">
    <section>
      <div>
        <div>
          <h2>Personal Information</h2>
        </div>

        <b-alert v-if="!emailVerified" variant="danger" :show="true" class="mb-1">
          <div class="alert-body font-small-2">
            <b-card-text class="d-flex justify-content-between align-items-center">
              Email verification pending
              <b-button v-if="!emailVerificationSent" variant="outline-primary" size="sm" @click="resendVerificationEmail">Resend Verification Email</b-button>
            </b-card-text>
          </div>
        </b-alert>

        <b-alert v-if="!phoneVerified" variant="danger" :show="true" class="mb-1">
          <div class="alert-body font-small-2">
            <b-card-text class="d-flex justify-content-between align-items-center">
              Phone verification pending
              <b-button v-if="!phoneVerificationSent" variant="outline-primary" size="sm" @click="requestPhoneVerification">Verify Phone</b-button>
            </b-card-text>
          </div>
        </b-alert>

        <div>
          <b-card>
            <div class="card-body">
              <validation-observer ref="newUserForm" #default="{invalid}">
                <b-form @submit.prevent="onUpdateUser">
                  <div class="mb-2">
                    <b-media class="text-center">
                      <b-avatar ref="previewEl" badge-variant="transparent" :src="avatarPath" :text="avatarText(`${userData.member_profile.first_name} ${userData.member_profile.last_name}`)" :variant="`light-${resolveStatusVariant(getValueFromSource(userData, 'status'))}`" size="10rem">
                        <template #badge>
                          <b-button variant="primary" class="btn-icon rounded-circle" @click="$refs.avatar.$el.childNodes[0].click()">
                            <feather-icon icon="CameraIcon" />
                          </b-button>
                        </template>
                      </b-avatar>
                    </b-media>
                  </div>

                  <div class="mb-2">
                    <div>
                      <b-form-file v-show="false" ref="avatar" accept="image/*" placeholder="" no-drop @input="uploadProfileImage" />
                    </div>
                  </div>

                  <!--first name -->
                  <div class="mb-1">
                    <b-form-group label="First Name" label-for="firstname">
                      <validation-provider #default="{ errors }" name="First Name" vid="firstname" rules="required">
                        <div class="custom-input">
                        <span class="leading">
                          <Person />
                        </span>
                          <input id="firstname" v-model="userData.member_profile.first_name" autocomplete="false">
                        </div>
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </div>

                  <!--last name -->
                  <div class="mb-1">
                    <b-form-group label="Last Name" label-for="lastname">
                      <validation-provider #default="{ errors }" name="Last Name" vid="lastname" rules="required">
                        <div class="custom-input">
                        <span class="leading">
                          <Person />
                        </span>
                          <input id="lastname" v-model="userData.member_profile.last_name" autocomplete="false">
                        </div>
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </div>

                  <!--Email -->
                  <div class="mb-1">
                    <b-form-group label="Email" label-for="email">
                      <validation-provider #default="{ errors }" name="Email" rules="required|email" vid="email">
                        <div class="custom-input">
                        <span class="leading">
                          <RegistrationMail />
                        </span>
                        <input id="email" v-model="userData.email" autocomplete="false">
                        <span v-if="getValueFromSource(currentUser, 'meta.email_verified', false)" class="trailing">
                          <small class="text-success">verified</small>
                          <feather-icon class="text-success" icon="CheckCircleIcon" />
                        </span>
                        <span v-else class="trailing">
                          <small class="text-danger">pending </small>
                          <feather-icon class="text-danger" icon="AlertCircleIcon" />
                        </span>
                        </div>
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </div>

                  <!--Phone -->
                  <div class="mb-1">
                    <b-form-group label="Phone" label-for="phone">
                      <validation-provider #default="{ errors }" name="Phone" rules="required" vid="phone">
                        <div class="custom-input">
                        <span class="leading">
                          <RegistrationPhone />
                        </span>
                        <input id="phone" v-model="userData.phone" autocomplete="false">
                        <span v-if="getValueFromSource(currentUser, 'meta.phone_verified', false)" class="trailing">
                          <small class="text-success">verified</small>
                          <feather-icon class="text-success" icon="CheckCircleIcon" />
                        </span>
                        <span v-else class="trailing">
                          <small class="text-danger">pending </small>
                          <feather-icon class="text-danger" icon="AlertCircleIcon" />
                        </span>
                        </div>
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </div>

                  <div>
                    <b-button class="py-1" type="submit" variant="primary" block :disabled="invalid">
                      Update Profile
                    </b-button>
                  </div>
                </b-form>
              </validation-observer>
            </div>
          </b-card>
        </div>
      </div>
    </section>
  </b-overlay>
</template>

<script>
import {
  BCard,
  BForm,
  BMedia,
  BAvatar,
  BButton,
  BOverlay,
  BFormFile,
  BDropdown,
  BFormGroup,
  BCardTitle,
  BDropdownItem,
  BAlert,
  BCardText,
  BBadge,
} from "bootstrap-vue";
import { cloneDeep, get } from "lodash";
import { MUTATE_USER_DATA, MUTATE_USER_MEMBER_PROFILE } from "@/store/config/mutation-keys";
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

import Cleave from 'vue-cleave-component';
import Person from '@/assets/svg/Person.svg';
import ResidentialIcon from '@/assets/svg/ResidentialIcon.svg';
import RegistrationMail from '@/assets/svg/RegistrationMail.svg';
import RegistrationPhone from '@/assets/svg/RegistrationPhone.svg';

export default {
  name: "Update",
  components: {
    BCard,
    BForm,
    BMedia,
    BBadge,
    Cleave,
    BAlert,
    Person,
    BAvatar,
    BButton,
    BOverlay,
    BDropdown,
    BFormFile,
    BCardTitle,
    BCardText,
    BFormGroup,
    BDropdownItem,
    ResidentialIcon,
    RegistrationMail,
    RegistrationPhone,
    ValidationObserver,
    ValidationProvider,
  },
  data() {
    return {
      loading: false,
      avatarPath: '',
      userData: {},
      cleaveDateOptions: {
        date: true,
        delimiter: '/',
        datePattern: ['Y', 'm', 'd'],
        blocks: [4, 2, 2],
        delimiters: ['-', '-', '-']
      },
      updatedImage: null,
      emailVerificationSent: false,
    }
  },
  computed: {
    isVerified() {
      return !(this.getValueFromSource(this.userData, 'meta.email_verified', false) &&
        this.getValueFromSource(this.userData, 'meta.phone_verified', false));
    },
    emailVerified() {
      return this.getValueFromSource(this.userData, 'meta.email_verified', false)
    },
    phoneVerified() {
      return this.getValueFromSource(this.userData, 'meta.phone_verified', false)
    },
    fullName() {
      return this.getValueFromSource(this.currentUser, 'member_profile.full_name', '');
    },
  },
  watch: {
    currentUser: {
      handler() {
        this.avatarPath = this.getValueFromSource(this.currentUser, 'member_profile.avatar.path', '');
      },
      deep: true,
      immediate: true
    }
  },
  created() {
    this.userData = cloneDeep(this.currentUser)
    this.mfa_conf = this.userData.mfa_conf;
  },
  methods: {
    async onUpdateUser() {
      try {
        this.loading = true;

        const formData = new FormData();
  
        if (this.updatedImage) {
          formData.append("avatar", this.updatedImage);
        }

        formData.append("first_name", this.userData.member_profile.first_name);
        formData.append("last_name", this.userData.member_profile.last_name);
        formData.append("email", this.userData.email);
        formData.append("phone", this.userData.phone);

        const response = await this.useJwt().mainService.auth.updateProfile(formData);
        const updated_user = get(response, 'data.data');
        this.$store.commit(`auth/${MUTATE_USER_DATA}`, updated_user.user);
        this.$store.commit(`auth/${MUTATE_USER_MEMBER_PROFILE}`, updated_user.profile);

        if (this.updatedImage) {
          this.updatedImage = null;
        }
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false
      }
    },
    async uploadProfileImage(file) {
      try {
        this.loading = true
        const reader = new FileReader()

        const formData = new FormData();
        formData.append("avatar", file);

        const response = await this.useJwt().mainService.auth.updateProfileAvatar(formData);
        const updated_user = get(response, 'data.data');
        this.$store.commit(`auth/${MUTATE_USER_DATA}`, updated_user);

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success!',
            icon: 'CheckCircleIcon',
            variant: 'success',
            text: "Profile image updated successfully"
          },
        }); 

        reader.addEventListener(
            'load',
            () => {
              this.avatarPath = reader.result
              this.updatedImage = file;
            },
            false,
        )

        if (file) {
          reader.readAsDataURL(file)
        }
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        }); 
      } finally {
        this.loading = false
      }
    },
    async requestPhoneVerification() {
      try {
        this.loading = true;
        await this.useJwt().mainService.auth.requestPhoneVerification();
        this.$router.push({ name: 'auth-verify-phone' });
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false;
      }
    },
    async resendVerificationEmail() {
      try {
        this.loading = true;
        await this.useJwt().mainService.auth.resendAccountConfirmationEmail({ username: this.userData.email });
        this.emailVerificationSent = true;
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success!',
            icon: 'CheckCircleIcon',
            variant: 'success',
            text: "Email verification sent successfully. Check your email for verification link"
          },
        });
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false;
      }
    }
  }
}
</script>

<style scoped>

</style>
